import { action } from 'mobx';

import type { Attributes, Model } from '@feathr/rachis';
import { Collection } from '@feathr/rachis';

import type { ICreativeAttributes } from './creatives_base';
import { BaseCreative, CreativeClass } from './creatives_base';
import type { DisplayCreative } from './creatives_display';
import { AdTagCreative, type IAdTagCreative } from './creatives_display_adtag';
import { BannersnackCreative, type IBannersnackCreative } from './creatives_display_bannersnack';
import { type IImageCreative, ImageCreative } from './creatives_display_image';
import { type IVideoCreative, VideoCreative } from './creatives_display_video';
import { GoogleAdsCreative, type IGoogleAdsCreative } from './creatives_google_ad';
import { FacebookImageCreative, type IFacebookImageCreative } from './creatives_meta_image';
import { FacebookVideoCreative, type IFacebookVideoCreative } from './creatives_meta_video';
import { PartnerMessageEmailCreative } from './creatives_partner_message_email';
import { ReferralBannerCreative, type TReferralBannerCreative } from './creatives_referral_banner';
import { ReferralEmailCreative, type TReferralEmailCreative } from './creatives_referral_email';
import { ReferralPageCreative, type TReferralPageCreative } from './creatives_referral_page';

/**
 * Union type of all creative classes.
 */
export type TCreative =
  | AdTagCreative
  | BannersnackCreative
  | BaseCreative
  | DisplayCreative
  | FacebookImageCreative
  | FacebookVideoCreative
  | GoogleAdsCreative
  | ImageCreative
  | PartnerMessageEmailCreative
  | ReferralBannerCreative
  | ReferralEmailCreative
  | ReferralPageCreative
  | VideoCreative;

/**
 * Factory function that returns the correct creative class based on the _cls attribute.
 * @param attributes - The creative attributes including _cls discriminator
 * @returns An instance of the appropriate creative subclass
 */
export function disambiguateCreative(attributes: Partial<ICreativeAttributes>): TCreative {
  switch (attributes._cls) {
    case CreativeClass.DisplayAdTag:
      return new AdTagCreative(attributes as Partial<IAdTagCreative>);

    case CreativeClass.DisplayBannersnack:
      return new BannersnackCreative(attributes as Partial<IBannersnackCreative>);

    case CreativeClass.DisplayImage:
      return new ImageCreative(attributes as Partial<IImageCreative>);

    case CreativeClass.DisplayVideo:
      return new VideoCreative(attributes as Partial<IVideoCreative>);

    case CreativeClass.FacebookImage:
      return new FacebookImageCreative(attributes as Partial<IFacebookImageCreative>);

    case CreativeClass.FacebookVideo:
      return new FacebookVideoCreative(attributes as Partial<IFacebookVideoCreative>);

    case CreativeClass.GoogleAd:
      return new GoogleAdsCreative(attributes as Partial<IGoogleAdsCreative>);

    case CreativeClass.PartnerMessageEmail:
      return new PartnerMessageEmailCreative(attributes);

    case CreativeClass.ReferralBanner:
      return new ReferralBannerCreative(attributes as Partial<TReferralBannerCreative>);

    case CreativeClass.ReferralEmail:
      return new ReferralEmailCreative(attributes as Partial<TReferralEmailCreative>);

    case CreativeClass.ReferralPage:
      return new ReferralPageCreative(attributes as Partial<TReferralPageCreative>);

    default:
      return new BaseCreative(attributes);
  }
}

/**
 * Collection class for managing creatives.
 */
export class Creatives extends Collection<TCreative> {
  @action.bound
  public getModel(attributes: Partial<Attributes<Model>> = {}): TCreative {
    return disambiguateCreative(attributes);
  }

  public getClassName(): string {
    return 'creatives';
  }
}
